window.CareersPage = function CareersPage({ setActiveTab }) {
  const [careersData, setCareersData] = React.useState({ benefits: [], positions: [] });
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [applied, setApplied] = React.useState(false);
  const [portalSubmitted, setPortalSubmitted] = React.useState(false);

  // Full Window Modal for Current Openings (Hidden by default)
  const [showCurrentOpeningsWindow, setShowCurrentOpeningsWindow] = React.useState(false);
  const [departmentFilter, setDepartmentFilter] = React.useState('All');
  const [isPaused, setIsPaused] = React.useState(false);

  // Auto-generated Reference ID
  const [refId, setRefId] = React.useState('');

  React.useEffect(() => {
    generateRefId();
    fetchCareers();
    const interval = setInterval(fetchCareers, 5000);
    const handleSync = () => fetchCareers();
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_careers_updated', handleSync);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_careers_updated', handleSync);
    };
  }, []);

  const fetchCareers = async () => {
    try {
      const res = await window.apiClient.get('careers.php');
      if (res.success && (res.positions || res.data)) {
        const rawPositions = res.positions || res.data || [];
        const publicPositions = rawPositions.filter(p => p.status !== 'draft');
        setCareersData({
          benefits: res.benefits || [],
          positions: publicPositions
        });
      }
    } catch (e) {
      console.warn('Careers API fetch error:', e);
    }
  };

  const generateRefId = () => {
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    setRefId(`NV-CAREER-2026-${randomCode}`);
  };

  const [applicantForm, setApplicantForm] = React.useState({
    fullName: '',
    email: '',
    phone: '',
    portfolioUrl: '',
    linkedinUrl: '',
    coverNote: ''
  });

  const [portalForm, setPortalForm] = React.useState({
    name: '',
    email: '',
    mobile: '',
    experience: '',
    portfolio: '',
    linkedinUrl: '',
    message: ''
  });

  // Interactive Local File Upload States (Start Your Journey)
  const [resumeFile, setResumeFile] = React.useState(null);
  const resumeInputRef = React.useRef(null);

  // Selected Job Modal File Upload States (View Role & Apply)
  const [applyResumeFile, setApplyResumeFile] = React.useState(null);
  const applyResumeInputRef = React.useRef(null);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setResumeFile({
      name: file.name,
      size: formatFileSize(file.size),
      rawFile: file
    });
  };

  const handleApplyFileSelect = (file) => {
    if (!file) return;
    setApplyResumeFile({
      name: file.name,
      size: formatFileSize(file.size),
      rawFile: file
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleApplyDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleApplyFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [portalEmailError, setPortalEmailError] = React.useState('');
  const [applyEmailError, setApplyEmailError] = React.useState('');
  const [portalPhoneError, setPortalPhoneError] = React.useState('');
  const [applyPhoneError, setApplyPhoneError] = React.useState('');

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setApplyEmailError('');
    setApplyPhoneError('');

    if (!window.validateEmail(applicantForm.email)) {
      setApplyEmailError('Please enter valid email');
      return;
    }

    if (!applicantForm.phone) {
      setApplyPhoneError('Phone number is required');
      return;
    }

    const clean = applicantForm.phone.replace(/\D/g, '');
    if (clean.length !== 10) {
      setApplyPhoneError('Phone number must be exactly 10 digits');
      return;
    }

    if (selectedJob && (selectedJob.status === 'closed' || selectedJob.enable_apply === false)) {
      alert(`Applications for '${selectedJob.title}' are currently closed.`);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('fullName', applicantForm.fullName);
      formData.append('email', applicantForm.email);
      formData.append('phone', applicantForm.phone);
      formData.append('portfolioUrl', applicantForm.portfolioUrl);
      formData.append('linkedinUrl', applicantForm.linkedinUrl);
      formData.append('coverNote', applicantForm.coverNote);
      formData.append('application_category', 'Specific Job Opening');
      if (selectedJob) {
        formData.append('career_id', selectedJob.id);
        formData.append('job_title', selectedJob.title);
      }
      if (applyResumeFile && applyResumeFile.rawFile) {
        formData.append('resume', applyResumeFile.rawFile);
      }

      const res = await window.apiClient.uploadFile('applications.php', formData);
      if (res && res.success === false) {
        alert(res.message || 'Applications for this position are currently closed.');
        return;
      }

      if (res && res.ref_id) setRefId(res.ref_id);
      setApplied(true);
      setApplicantForm({ fullName: '', email: '', phone: '', portfolioUrl: '', linkedinUrl: '', coverNote: '' });
      setApplyResumeFile(null);
      setApplyEmailError('');
      setApplyPhoneError('');
      try { window.dispatchEvent(new CustomEvent('novatek_app_added')); } catch(e){}
    } catch (err) {
      console.warn('Backend upload warning:', err);
      alert(err.message || 'Submission error. Please check position availability.');
    }
    setTimeout(() => {
      setApplied(false);
      setSelectedJob(null);
    }, 2500);
  };

  const handlePortalSubmit = async (e) => {
    e.preventDefault();
    setPortalEmailError('');
    setPortalPhoneError('');

    if (!window.validateEmail(portalForm.email)) {
      setPortalEmailError('Please enter valid email');
      return;
    }

    if (!portalForm.mobile) {
      setPortalPhoneError('Mobile number is required');
      return;
    }

    const clean = portalForm.mobile.replace(/\D/g, '');
    if (clean.length !== 10) {
      setPortalPhoneError('Mobile number must be exactly 10 digits');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', portalForm.name);
      formData.append('email', portalForm.email);
      formData.append('mobile', portalForm.mobile);
      formData.append('experience', portalForm.experience);
      formData.append('portfolio', portalForm.portfolio);
      formData.append('linkedin_url', portalForm.linkedinUrl);
      formData.append('message', portalForm.message);
      formData.append('application_category', 'Start Your Journey Portal');
      if (resumeFile && resumeFile.rawFile) formData.append('resume', resumeFile.rawFile);

      const res = await window.apiClient.uploadFile('applications.php', formData);
      if (res && res.ref_id) setRefId(res.ref_id);
      try { window.dispatchEvent(new CustomEvent('novatek_app_added')); } catch(e){}
    } catch (err) {
      console.warn('Portal submission warning:', err);
    }

    setPortalSubmitted(true);
    setTimeout(() => {
      setPortalSubmitted(false);
      generateRefId();
      setResumeFile(null);
      setPortalForm({
        name: '',
        email: '',
        mobile: '',
        experience: '',
        portfolio: '',
        linkedinUrl: '',
        message: ''
      });
      setPortalEmailError('');
      setPortalPhoneError('');
    }, 4000);
  };

  const departments = ['All', ...new Set((careersData.positions || []).map(p => p.department))];

  const filteredPositions = departmentFilter === 'All'
    ? (careersData.positions || [])
    : (careersData.positions || []).filter(p => p.department === departmentFilter);

  return (
    <div className="bg-surface min-h-screen space-y-24 pb-24">
      
      {/* Careers Hero Section Replicating Exact Reference Image with floating Live Openings */}
      <section className="relative overflow-hidden bg-slate-950 text-white min-h-[480px] sm:min-h-[580px] flex items-center">
        {/* Robotics Assembly & Test Facility Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0"
          style={{ backgroundImage: `url("/api/storage/banners/careers_hero_bg.png")` }}
        >
          {/* Dark Blue Overlay Filter matching the reference screenshot */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#031329]/95 via-[#031329]/85 to-[#031329]/65"></div>
        </div>

        <div className="relative z-10 w-full max-w-none px-6 md:px-12 lg:px-16 py-16 sm:py-24 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12">
          
          {/* Left Column (Hero Content) */}
          <div className="space-y-6 lg:max-w-2xl">
            <span 
              className="font-mono text-xs sm:text-sm font-extrabold tracking-widest uppercase block"
              style={{ color: '#ffbf00' }}
            >
              FUTURE ENGINEERING
            </span>

            <div className="space-y-3">
              <h1 className="font-sans text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight">
                Join Us to Build the Future of Industry.
              </h1>
              <p className="font-sans text-slate-300 text-lg sm:text-xl lg:text-2xl font-normal leading-relaxed pt-1">
                Your Talent Deserves the Right Opportunity.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <button 
                onClick={() => setShowCurrentOpeningsWindow(true)}
                className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs uppercase px-6 py-3.5 rounded font-bold tracking-wider transition-all shadow-lg inline-flex items-center gap-2 cursor-pointer"
              >
                <span>VIEW OPEN POSITIONS</span>
                <span className="material-symbols-outlined text-base">open_in_new</span>
              </button>
              <button 
                onClick={() => {
                  if (setActiveTab) {
                    window.scrollToVisionMission = true;
                    setActiveTab('home');
                  }
                }}
                className="border border-slate-600 hover:border-orange-500 bg-slate-900/80 hover:bg-slate-900 text-white font-mono text-xs uppercase px-6 py-3.5 rounded font-bold tracking-wider transition-all shadow-lg cursor-pointer"
              >
                OUR MISSION
              </button>
            </div>
          </div>

          {/* Right Column (Floating Live Positions Card in Premium Light Theme - Expanded Length) */}
          <div className="w-full lg:w-[660px] bg-white/95 backdrop-blur-md rounded-2xl border-2 p-6 sm:p-7 shadow-2xl space-y-5 shrink-0 animate-fadeIn relative z-10 self-center text-slate-900 animate-pulseGlow">
            <style>{`
              @keyframes pulseGlow {
                0%, 100% {
                  border-color: #f97316;
                  box-shadow: 0 0 15px rgba(249, 115, 22, 0.4);
                }
                50% {
                  border-color: #ffedd5;
                  box-shadow: 0 0 25px rgba(249, 115, 22, 0.7);
                }
              }
              .animate-pulseGlow {
                animation: pulseGlow 2.5s infinite ease-in-out;
              }
            `}</style>
            <div className="flex justify-between items-center pb-3.5 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <div className="relative flex h-3 w-3 items-center justify-center shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </div>
                <span className="font-mono text-sm sm:text-base font-bold text-slate-800 uppercase tracking-widest pl-1.5">
                  Live Openings
                </span>
              </div>
              <span className="bg-slate-100 border border-slate-200 text-secondary font-mono text-xs font-bold px-2.5 py-0.5 rounded">
                {(careersData.positions || []).length} ACTIVE
              </span>
            </div>

            { (careersData.positions || []).length === 0 ? (
              <div className="border border-slate-100/50 rounded-xl bg-slate-50/50 p-8 flex flex-col items-center justify-center min-h-[180px]">
                <span className="material-symbols-outlined text-slate-400 text-3xl mb-2">work_off</span>
                <p className="text-center text-slate-500 font-mono text-sm font-semibold">
                  Currently no openings are available.
                </p>
              </div>
            ) : (
              <div className="relative overflow-hidden max-h-[300px] border border-slate-100/50 rounded-xl bg-slate-50/50 p-2">
                <div 
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                  style={{ 
                    animationPlayState: isPaused ? 'paused' : 'running',
                    animationDuration: '20s'
                  }}
                  className="animate-endlessScroll flex flex-col gap-2.5"
                >
                  {[...(careersData.positions || []), ...(careersData.positions || [])].map((pos, idx) => {
                    const isClosed = pos.status === 'closed';
                    return (
                      <div 
                        key={`${pos.id}-${idx}`}
                        onClick={() => setSelectedJob(pos)}
                        className={`group border p-3.5 rounded-lg cursor-pointer transition-all duration-300 space-y-1.5 shrink-0 shadow-xs ${
                          isClosed
                            ? 'bg-slate-100/70 hover:bg-slate-100 border-slate-200 opacity-80'
                            : 'bg-white hover:bg-orange-50/20 border-slate-200 hover:border-secondary/40'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex items-center gap-1.5 overflow-hidden">
                            {isClosed && (
                              <span className="shrink-0 font-mono text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-1 py-0.5 rounded uppercase">
                                Closed
                              </span>
                            )}
                            <h4 className={`font-sans text-sm sm:text-base font-bold transition-colors line-clamp-1 ${
                              isClosed ? 'text-slate-500' : 'text-slate-800 group-hover:text-secondary'
                            }`}>
                              {pos.title}
                            </h4>
                          </div>
                          <span className="material-symbols-outlined text-slate-400 group-hover:text-secondary text-lg transition-transform group-hover:translate-x-0.5">
                            chevron_right
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs font-mono text-slate-500">
                          <span className="truncate max-w-[170px]">{pos.department}</span>
                          <span className="text-slate-400">{pos.experience}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowCurrentOpeningsWindow(true)}
              className="w-full bg-secondary hover:bg-orange-600 text-white font-mono text-xs sm:text-sm font-bold py-3.5 rounded-xl transition-all text-center flex items-center justify-center gap-1.5 shadow-md shadow-orange-500/15 active:scale-95 cursor-pointer"
            >
              <span>Explore All Roles</span>
              <span className="material-symbols-outlined text-base font-bold">arrow_forward</span>
            </button>
          </div>

        </div>
      </section>

      {/* Why Work With Us (Perks & Benefits) */}
      <section id="why-work-with-us" className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8 pt-4">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <h2 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900">Why Work With Us</h2>
          <p className="font-sans text-slate-600 text-base">We provide an environment where high-level engineering flourishes.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {careersData.benefits.map((perk, idx) => (
            <div key={idx} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-3 hover:shadow-md transition-shadow">
              <span className="material-symbols-outlined text-secondary text-3xl">{perk.icon}</span>
              <h3 className="font-sans text-lg font-bold text-slate-900">{perk.title}</h3>
              <p className="font-sans text-slate-600 text-xs leading-relaxed">{perk.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Start Your Journey Recruitment Portal Section (Matching Reference Screenshot) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column (5 Cols) */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="font-sans text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
                Start Your Journey.
              </h2>
              <p className="font-sans text-slate-600 text-sm sm:text-base leading-relaxed">
                Submit your credentials to our recruitment portal. Our technical team reviews every application for alignment with our current precision projects.
              </p>
            </div>

            {/* Stepper */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="h-8 w-8 bg-slate-950 text-white rounded-full flex items-center justify-center font-mono font-bold text-xs shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <h4 className="font-sans text-sm font-bold text-slate-900">Upload Profile</h4>
                  <p className="font-sans text-xs text-slate-500 mt-0.5">Provide your technical history and portfolio links.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="h-8 w-8 bg-slate-950 text-white rounded-full flex items-center justify-center font-mono font-bold text-xs shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <h4 className="font-sans text-sm font-bold text-slate-900">Technical Review</h4>
                  <p className="font-sans text-xs text-slate-500 mt-0.5">Domain experts assess your engineering capabilities.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="h-8 w-8 bg-slate-950 text-white rounded-full flex items-center justify-center font-mono font-bold text-xs shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <h4 className="font-sans text-sm font-bold text-slate-900">Onboarding</h4>
                  <p className="font-sans text-xs text-slate-500 mt-0.5">Integration into our global industrial ecosystem.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column Form Card (7 Cols) */}
          <div className="lg:col-span-7 bg-white p-8 sm:p-10 rounded-xl border border-slate-300 shadow-sm space-y-6">
            {portalSubmitted ? (
              <div className="py-12 text-center space-y-4 animate-fadeIn">
                <div className="h-16 w-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <span className="material-symbols-outlined text-4xl">check_circle</span>
                </div>
                <h4 className="font-sans text-2xl font-bold text-slate-900">Application Received!</h4>
                <p className="text-slate-600 text-sm max-w-md mx-auto leading-relaxed">
                  Thank you, <strong>{portalForm.name}</strong>. Your application has been logged into the NOVATEK technical portal.
                </p>
                <div className="bg-slate-50 p-4 rounded border border-slate-200 max-w-xs mx-auto inline-block">
                  <span className="font-mono text-xs text-slate-500 uppercase block">Application Reference ID</span>
                  <span className="font-mono text-base font-bold text-secondary">{refId}</span>
                </div>
                <p className="text-xs text-slate-400">A confirmation with this Reference ID will be dispatched to your email.</p>
              </div>
            ) : (
              <form onSubmit={handlePortalSubmit} className="space-y-5">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      NAME *
                    </label>
                    <input
                      type="text"
                      required
                      value={portalForm.name}
                      onChange={(e) => setPortalForm({ ...portalForm, name: e.target.value })}
                      placeholder="Full Name"
                      className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      EMAIL *
                    </label>
                    <input
                      type="text"
                      required
                      value={portalForm.email}
                      onChange={(e) => {
                        setPortalForm({ ...portalForm, email: e.target.value });
                        if (portalEmailError) setPortalEmailError('');
                      }}
                      onBlur={() => {
                        if (portalForm.email && !window.validateEmail(portalForm.email)) {
                          setPortalEmailError('Please enter valid email');
                        }
                      }}
                      placeholder="email@professional.com"
                      className={`w-full px-3.5 py-2.5 text-sm border rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans ${
                        portalEmailError ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
                      }`}
                    />
                    {portalEmailError && (
                      <p className="text-red-500 font-mono text-xs font-semibold mt-1 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">error</span>
                        <span>{portalEmailError}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      MOBILE NUMBER *
                    </label>
                    <input
                      type="tel"
                      required
                      value={portalForm.mobile}
                      onChange={(e) => {
                        const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPortalForm({ ...portalForm, mobile: clean });
                        if (portalPhoneError) setPortalPhoneError('');
                      }}
                      placeholder="e.g. 9800000000"
                      className={`w-full px-3.5 py-2.5 text-sm border rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans ${
                        portalPhoneError ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
                      }`}
                    />
                    {portalPhoneError && (
                      <p className="text-red-500 font-mono text-xs font-semibold mt-1 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">error</span>
                        <span>{portalPhoneError}</span>
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      EXPERIENCE (IN YEARS)
                    </label>
                    <input
                      type="number"
                      value={portalForm.experience}
                      onChange={(e) => setPortalForm({ ...portalForm, experience: e.target.value })}
                      placeholder="5"
                      className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    PORTFOLIO (TEXT/LINK INPUT)
                  </label>
                  <input
                    type="text"
                    value={portalForm.portfolio}
                    onChange={(e) => setPortalForm({ ...portalForm, portfolio: e.target.value })}
                    placeholder="https://github.com/profile or technical portfolio link"
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans"
                  />
                </div>

                {/* Drag & Drop Resume Upload & LinkedIn URL */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  
                  {/* UPLOAD RESUME */}
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      UPLOAD RESUME
                    </label>

                    <input
                      type="file"
                      ref={resumeInputRef}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
                    />

                    {resumeFile ? (
                      <div className="border border-emerald-500 bg-emerald-50/40 rounded-sm p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <span className="material-symbols-outlined text-emerald-600 text-xl shrink-0">
                            picture_as_pdf
                          </span>
                          <div className="truncate">
                            <span className="font-sans text-xs font-bold text-slate-800 block truncate">{resumeFile.name}</span>
                            <span className="font-mono text-[10px] text-slate-500">{resumeFile.size}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setResumeFile(null)}
                          className="text-slate-400 hover:text-red-500 p-1"
                        >
                          <span className="material-symbols-outlined text-base">close</span>
                        </button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => resumeInputRef.current && resumeInputRef.current.click()}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className="border-2 border-dashed border-slate-300 hover:border-secondary rounded-sm p-6 text-center cursor-pointer transition-colors bg-slate-50/50 hover:bg-orange-50/20 group"
                      >
                        <span className="material-symbols-outlined text-slate-400 group-hover:text-secondary text-2xl mb-1 block transition-colors">upload_file</span>
                        <span className="font-sans text-xs text-slate-600 group-hover:text-slate-900 block font-medium">Drag or browse PDF</span>
                      </div>
                    )}
                  </div>

                  {/* LINKEDIN URL */}
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                      LINKEDIN URL
                    </label>

                    <input
                      type="url"
                      value={portalForm.linkedinUrl}
                      onChange={(e) => setPortalForm({ ...portalForm, linkedinUrl: e.target.value })}
                      placeholder="https://linkedin.com/in/your-profile"
                      className="w-full px-3.5 py-6 text-sm border border-slate-300 rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans"
                    />
                  </div>

                </div>

                {/* Message Label: MESSAGE */}
                <div>
                  <label className="block font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                    MESSAGE
                  </label>
                  <textarea
                    rows="3"
                    value={portalForm.message}
                    onChange={(e) => setPortalForm({ ...portalForm, message: e.target.value })}
                    placeholder="Additional details about your technical background..."
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none font-sans"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-slate-950 hover:bg-slate-900 text-white font-mono text-xs uppercase tracking-widest py-4 font-bold transition-all shadow cursor-pointer"
                >
                  SUBMIT APPLICATION
                </button>

                <p className="text-center font-mono text-[11px] text-slate-500 pt-1">
                  Reference ID: <span className="font-bold text-slate-700">{refId}</span>
                </p>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* FULL SCREEN DEDICATED NOVATEK CAREERS PORTAL PAGE OVERLAY */}
      {showCurrentOpeningsWindow && (
        <div className="fixed inset-0 z-50 bg-slate-50 text-slate-900 overflow-y-auto animate-fadeIn flex flex-col">
          
          {/* Top Header Bar */}
          <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-secondary text-2xl">work</span>
              <div>
                <h2 className="font-sans text-lg sm:text-xl font-bold text-slate-900">
                  Current Technical Openings
                </h2>
              </div>
            </div>

            <button
              onClick={() => setShowCurrentOpeningsWindow(false)}
              className="bg-white hover:bg-slate-50 text-slate-700 font-mono text-xs font-bold uppercase px-4 py-2 rounded-lg border border-slate-300 transition-all flex items-center gap-2 cursor-pointer shadow-sm hover:text-slate-900"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              <span>back</span>
            </button>
          </div>

          {/* Portal Content Area */}
          <div className="max-w-6xl mx-auto w-full px-6 py-10 space-y-8 flex-grow">
            
            {/* Header Title & Department Filters */}
            <div className="space-y-6 bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <div className="max-w-3xl space-y-2">
                <span className="font-mono text-xs font-bold text-secondary uppercase tracking-widest">
                  ENGINEERING ROLES & VACANCIES
                </span>
                <h1 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900">
                  Open Technical Positions
                </h1>
                <p className="font-sans text-slate-600 text-sm sm:text-base">
                  Explore available engineering opportunities across mechanical, automation, and aerospace divisions.
                </p>
              </div>

              {/* Department Filter Pills */}
              <div className="flex flex-wrap gap-2 pt-2">
                {departments.map((dept) => (
                  <button
                    key={dept}
                    onClick={() => setDepartmentFilter(dept)}
                    className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer border ${
                      departmentFilter === dept
                        ? 'bg-secondary text-white border-secondary shadow-md'
                        : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-900'
                    }`}
                  >
                    {dept}
                  </button>
                ))}
              </div>
            </div>

            {/* Positions List */}
            <div className="space-y-4">
              {filteredPositions.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center text-slate-500 font-mono text-sm">
                  No technical positions match the selected department filter.
                </div>
              ) : (
                filteredPositions.map((job) => {
                  const isClosed = job.status === 'closed' || job.enable_apply === false;
                  return (
                    <div 
                      key={job.id} 
                      className={`p-6 sm:p-8 rounded-2xl border transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-6 ${
                        isClosed ? 'bg-slate-100/60 border-red-200/40 opacity-80' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md shadow-sm'
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-mono text-xs font-bold text-secondary bg-orange-50 border border-orange-200 px-2.5 py-0.5 rounded">
                            {job.department}
                          </span>
                          <span className="font-mono text-xs text-slate-500">{job.type}</span>
                          <span className="font-mono text-xs text-slate-500">• {job.experience}</span>
                          {isClosed ? (
                            <span className="font-mono text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2.5 py-0.5 rounded flex items-center gap-1 uppercase">
                              <span className="material-symbols-outlined text-xs">lock</span>
                              APPLICATION CLOSED
                            </span>
                          ) : (
                            <span className="font-mono text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded flex items-center gap-1 uppercase">
                              <span className="material-symbols-outlined text-xs">check_circle</span>
                              ACCEPTING APPLICATIONS
                            </span>
                          )}
                        </div>
                        <h3 className="font-sans text-2xl font-bold text-slate-900">{job.title}</h3>
                        <p className="font-sans text-slate-600 text-sm max-w-2xl">{job.summary}</p>
                      </div>

                      {isClosed ? (
                        <button
                          onClick={() => setSelectedJob(job)}
                          className="bg-white hover:bg-slate-50 text-slate-600 px-6 py-3 rounded-xl font-medium text-sm transition-all shadow-sm shrink-0 flex items-center gap-2 cursor-pointer border border-slate-300 hover:text-slate-900"
                        >
                          <span>View Role Details</span>
                          <span className="material-symbols-outlined text-base">info</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedJob(job)}
                          className="bg-secondary hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-bold text-xs font-mono uppercase tracking-wider transition-all shadow-md shrink-0 flex items-center gap-2 cursor-pointer"
                        >
                          <span>View Role & Apply</span>
                          <span className="material-symbols-outlined text-base">arrow_forward</span>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

          </div>

          {/* Footer Bar */}
          <div className="bg-white border-t border-slate-200 p-6 text-center text-xs font-mono text-slate-500 shrink-0">
            NOVATEK Industrial Solutions • Engineering Careers Portal
          </div>

        </div>
      )}

      {/* Job Detail & Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">
            
            <div className="bg-slate-50 text-slate-900 p-6 flex justify-between items-center border-b border-slate-200 shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-secondary uppercase font-bold">{selectedJob.department}</span>
                  {(selectedJob.status === 'closed' || selectedJob.enable_apply === false) && (
                    <span className="font-mono text-[10px] bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded font-bold uppercase">
                      CLOSED
                    </span>
                  )}
                </div>
                <h3 className="font-sans text-2xl font-bold text-slate-900">{selectedJob.title}</h3>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-slate-400 hover:text-slate-700 p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              {applied ? (
                <div className="py-12 text-center space-y-4">
                  <div className="h-16 w-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                    <span className="material-symbols-outlined text-4xl">check_circle</span>
                  </div>
                  <h4 className="font-sans text-2xl font-bold text-slate-900">Application Submitted!</h4>
                  <p className="text-slate-600 text-sm max-w-md mx-auto">
                    Thank you for applying for {selectedJob.title}. Our HR and Engineering Talent team will review your profile. Reference ID: {refId}.
                  </p>
                </div>
              ) : selectedJob.status === 'closed' || selectedJob.enable_apply === false ? (
                <div className="space-y-6">
                  <div className="space-y-4">
                    {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-sans text-base font-bold text-slate-900">Key Responsibilities</h4>
                        <ul className="space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                          {selectedJob.responsibilities.map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-sans text-base font-bold text-slate-900">Required Skills</h4>
                        <ul className="space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                          {selectedJob.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Closed Application Banner */}
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center space-y-3 pt-6">
                    <div className="h-12 w-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                      <span className="material-symbols-outlined text-2xl">lock</span>
                    </div>
                    <h4 className="font-sans text-xl font-bold text-red-900">Applications Closed</h4>
                    <p className="text-red-700 text-sm max-w-md mx-auto leading-relaxed">
                      This job vacancy has been closed by NOVATEK HR & Engineering Talent Management. We are currently no longer accepting new candidate applications for this role.
                    </p>
                    <div className="pt-2">
                      <button
                        onClick={() => setSelectedJob(null)}
                        className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-mono font-bold uppercase transition-all shadow cursor-pointer"
                      >
                        Close Details Window
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-sans text-base font-bold text-slate-900">Key Responsibilities</h4>
                        <ul className="space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                          {selectedJob.responsibilities.map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-sans text-base font-bold text-slate-900">Required Skills</h4>
                        <ul className="space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                          {selectedJob.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleApplySubmit} className="space-y-4 pt-4 border-t border-slate-200">
                    <h4 className="font-sans text-lg font-bold text-slate-900">Application Details</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-xs font-semibold text-slate-700 uppercase mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={applicantForm.fullName}
                          onChange={(e) => setApplicantForm({ ...applicantForm, fullName: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-secondary outline-none"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-xs font-semibold text-slate-700 uppercase mb-1">
                          Email Address *
                        </label>
                        <input
                          type="text"
                          required
                          value={applicantForm.email}
                          onChange={(e) => {
                            setApplicantForm({ ...applicantForm, email: e.target.value });
                            if (applyEmailError) setApplyEmailError('');
                          }}
                          onBlur={() => {
                            if (applicantForm.email && !window.validateEmail(applicantForm.email)) {
                              setApplyEmailError('Please enter valid email');
                            }
                          }}
                          className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-secondary outline-none ${
                            applyEmailError ? 'border-red-500 bg-red-50/20' : 'border-slate-300'
                          }`}
                        />
                        {applyEmailError && (
                          <p className="text-red-500 font-mono text-xs font-semibold mt-1 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">error</span>
                            <span>{applyEmailError}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-xs font-semibold text-slate-700 uppercase mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          required
                          value={applicantForm.phone}
                          onChange={(e) => {
                            const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setApplicantForm({ ...applicantForm, phone: clean });
                            if (applyPhoneError) setApplyPhoneError('');
                          }}
                          placeholder="e.g. 9800000000"
                          className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 outline-none ${
                            applyPhoneError ? 'border-red-500 bg-red-50/20' : 'border-slate-300 focus:ring-secondary'
                          }`}
                        />
                        {applyPhoneError && (
                          <p className="text-red-500 font-mono text-xs font-semibold mt-1 flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">error</span>
                            <span>{applyPhoneError}</span>
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block font-mono text-xs font-semibold text-slate-700 uppercase mb-1">
                          LinkedIn / Portfolio URL
                        </label>
                        <input
                          type="url"
                          value={applicantForm.portfolioUrl}
                          onChange={(e) => setApplicantForm({ ...applicantForm, portfolioUrl: e.target.value })}
                          placeholder="https://..."
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-secondary outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-mono text-xs font-semibold text-slate-700 uppercase mb-1">
                        Brief Engineering Cover Note
                      </label>
                      <textarea
                        rows="2"
                        value={applicantForm.coverNote}
                        onChange={(e) => setApplicantForm({ ...applicantForm, coverNote: e.target.value })}
                        placeholder="Mention relevant projects in CAD, FEA, PLC, or dynamic test bench design..."
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-secondary outline-none"
                      ></textarea>
                    </div>

                    {/* File Dropzone */}
                    <div className="pt-2">
                      
                      {/* UPLOAD RESUME */}
                      <div>
                        <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase mb-1">
                          UPLOAD RESUME *
                        </label>

                        <input
                          type="file"
                          ref={applyResumeInputRef}
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => e.target.files && handleApplyFileSelect(e.target.files[0])}
                        />

                        {applyResumeFile ? (
                          <div className="border border-emerald-500 bg-emerald-50/40 rounded p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 overflow-hidden">
                              <span className="material-symbols-outlined text-emerald-600 text-lg shrink-0">
                                picture_as_pdf
                              </span>
                              <div className="truncate">
                                <span className="font-sans text-xs font-bold text-slate-800 block truncate">{applyResumeFile.name}</span>
                                <span className="font-mono text-[10px] text-slate-500">{applyResumeFile.size}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setApplyResumeFile(null)}
                              className="text-slate-400 hover:text-red-500 p-1"
                            >
                              <span className="material-symbols-outlined text-base">close</span>
                            </button>
                          </div>
                        ) : (
                          <div 
                            onClick={() => applyResumeInputRef.current && applyResumeInputRef.current.click()}
                            onDragOver={handleDragOver}
                            onDrop={handleApplyDrop}
                            className="border-2 border-dashed border-slate-300 hover:border-secondary rounded p-4 text-center cursor-pointer transition-colors bg-slate-50 hover:bg-orange-50/20 group w-full"
                          >
                            <span className="material-symbols-outlined text-slate-400 group-hover:text-secondary text-xl mb-0.5 block transition-colors">upload_file</span>
                            <span className="font-sans text-xs text-slate-700 group-hover:text-slate-900 block font-medium">Drag or browse PDF</span>
                            <span className="font-mono text-[10px] text-slate-400 block mt-0.5">Max 10MB</span>
                          </div>
                        )}
                      </div>

                    </div>

                    <div className="pt-3 flex justify-end gap-3 border-t border-slate-200">
                      <button
                        type="button"
                        onClick={() => setSelectedJob(null)}
                        className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded font-medium hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 text-sm font-medium bg-secondary text-white rounded hover:bg-orange-600 shadow cursor-pointer transition-all"
                      >
                        Submit Application
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
